import { AttributeDataType, CustomField } from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";
import {
  convertDataTypeBooleanToBoolean,
  convertDataTypeBooleanToString
} from "../shared/utils/convertDataTypeBoolean";

/**
 * A contractor's custom-field values, keyed by field key.
 *
 * Declared here rather than imported from v1 `@wingspanhq/payments`
 * (`ICustomFields` / `ICustomFieldValue`): the v3 SDK has no usable twin —
 * `CustomFieldsValue` is an empty interface, and `getPayeeCustomFields` returns
 * the same keyed map untyped. The *definitions* are v3 (`CustomField` above);
 * only the value map needs a local shape.
 */
export type CustomFieldValue = string | boolean | null | Array<string>;
export type CustomFieldValues = Record<string, CustomFieldValue>;

export function getYupValidationSchema(formFields: CustomField[]) {
  const validationSchema: any = {};
  formFields.forEach(field => {
    if (!field.key) {
      return;
    }
    let validationRule: any = Yup.string();
    switch (field.dataType) {
      case AttributeDataType.STRING:
        if (field.isRequired) {
          validationRule = validationRule.required(`${field.name} is required`);
        }
        break;
      case AttributeDataType.BOOLEAN:
        validationRule = Yup.boolean();
        if (field.isRequired) {
          validationRule = validationRule.required(`${field.name} is required`);
        }
        break;
      default:
        if (field.isRequired) {
          validationRule = validationRule.required(`${field.name} is required`);
        }
    }
    validationSchema[field.key] = validationRule;
  });
  return validationSchema;
}

export function getDefaultValuesForAdditionalData(
  formFields: CustomField[],
  labels?: CustomFieldValues
) {
  const defaultValues: CustomFieldValues = {};
  formFields.forEach(field => {
    if (!field.key) {
      return;
    }
    switch (field.dataType) {
      case AttributeDataType.STRING:
        defaultValues[field.key] = labels?.[field.key] || "";
        break;
      case AttributeDataType.BOOLEAN:
        defaultValues[field.key] = convertDataTypeBooleanToBoolean(
          labels?.[field.key]
        );
        break;
    }
  });
  return defaultValues;
}

export function prepareCollaboratorLabelsRequest(
  formFields: CustomField[],
  values: { [key: string]: any }
) {
  const labels: any = {};
  formFields.forEach(field => {
    if (!field.key) {
      return;
    }
    switch (field.dataType) {
      case AttributeDataType.STRING:
        labels[field.key] = values[field.key] ? String(values[field.key]) : "";
        break;

      case AttributeDataType.BOOLEAN:
        labels[field.key] = convertDataTypeBooleanToString(values[field.key]);
        break;
    }
  });
  return labels;
}

function booleanFieldToFilterRequest(filterValue: string[]) {
  if (filterValue.length === 1) {
    if (filterValue[0] === "true") {
      return "true";
    } else {
      return {
        "!=": "true"
      };
    }
  }

  return;
}

function stringFieldToFilterRequest(filterValue: string) {
  return {
    contains: filterValue
  };
}

export function customFieldToFiltersRequest(
  customField: CustomField,
  filterValue?: string | string[]
) {
  if (!filterValue) {
    return;
  }

  switch (customField.dataType) {
    case AttributeDataType.BOOLEAN:
      if (Array.isArray(filterValue)) {
        return booleanFieldToFilterRequest(filterValue);
      } else {
        console.error("Boolean field should be an array");
        return;
      }
    default:
      if (typeof filterValue === "string") {
        return stringFieldToFilterRequest(filterValue);
      } else {
        console.error("String field should be a string");
      }
  }
}
